import { useAudioControls } from '@/lib/audio/audio-player-provider';
import type { TokenHighlightController } from '@/lib/audio/token-highlight-controller';
import type { TranscriptionToken } from '@/lib/transcription/types';
import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface TokenRendererProps {
  /** Array of tokens to render */
  tokens: TranscriptionToken[];

  /** Starting index offset for data attributes */
  startIndex?: number;

  /** Token highlight controller instance */
  highlightController: TokenHighlightController;

  /** Global token indices where a segment break should be rendered */
  segmentBreakIndices?: Set<number>;

  /** Container element type */
  as?: 'div' | 'span';

  /** Optional callback when a token is clicked */
  onTokenClick?: (token: TranscriptionToken) => void;

  /** Optional CSS class for the container */
  className?: string;
}

/**
 * Renders transcription tokens as clickable, highlightable spans.
 *
 * Features:
 * - Click to seek to token start time
 * - Automatically registers tokens with highlight controller
 * - DOM-based highlighting (no React rerenders)
 * - Preserves token order and spacing
 */
export const TokenRenderer = ({
  tokens,
  startIndex = 0,
  highlightController,
  segmentBreakIndices,
  as = 'div',
  onTokenClick,
  className = '',
}: TokenRendererProps) => {
  const { t } = useTranslation('home');
  const { seek } = useAudioControls();
  const tokenRefsMap = useRef<Map<number, HTMLSpanElement>>(new Map());
  const Container = as;

  /**
   * Register all tokens with the controller on mount/token change.
   * useLayoutEffect ensures refs are populated before we register (runs synchronously after DOM commit).
   */
  useLayoutEffect(() => {
    tokens.forEach((token, index) => {
      const element = tokenRefsMap.current.get(index);
      if (element) {
        highlightController.registerToken(token, element);
      }
    });

    return () => {
      tokenRefsMap.current.forEach((element) => {
        highlightController.unregisterToken(element);
      });
      tokenRefsMap.current.clear();
    };
  }, [tokens, highlightController]);

  /**
   * Handle token click to seek to that token's start time
   */
  const handleTokenClick = (token: TranscriptionToken) => {
    seek(token.startMs);
    onTokenClick?.(token);
  };

  return (
    <Container className={className}>
      {tokens.map((token, index) => {
        const tokenIndex = startIndex + index;
        return (
          <span key={`${token.startMs}-${tokenIndex}`}>
            {segmentBreakIndices?.has(tokenIndex) && (
              <span
                className="my-3 items-center gap-3"
                style={{ display: 'flex' }}>
                <span className="h-px flex-1 bg-gray-300" />
                <span className="text-xs whitespace-nowrap text-gray-400">
                  {t('paused')}
                </span>
                <span className="h-px flex-1 bg-gray-300" />
              </span>
            )}
            <span
              ref={(el) => {
                if (el) {
                  tokenRefsMap.current.set(index, el);
                }
              }}
              onClick={() => handleTokenClick(token)}
              className="data-[highlighted=true]:bg-primary/30 ring-primary hover:bg-primary/20 cursor-pointer rounded-[8px] transition-colors select-text data-[highlighted=true]:ring"
              data-token-index={tokenIndex}
              data-token-start={token.startMs}
              data-token-end={token.endMs}>
              {token.text}
            </span>
          </span>
        );
      })}
    </Container>
  );
};
